// /search — global search results page (T059 issues, T271 projects).
//
// URL shape: /search?q=<query>[&type=issues|projects][&from=<offset>]
//
// "Issues" tab: fires GET /api/v1/search/issues?q=… (OpenSearch-backed),
// paginates via ?from offset (server page size fixed at 20).
// "Projects" tab (T271): loads GET /api/v1/projects and text-filters client-
// side — no separate search endpoint exists for projects.

import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  ArrowLeft,
  ArrowRight,
  Bug,
  Eye,
  GitBranch,
  KeyRound,
  Loader2,
  Search,
  ShieldAlert,
  Sprout,
} from 'lucide-react';
import {
  IssueHit,
  IssueKind,
  Project,
  SearchResult,
  buildSearchURL,
  fetcher,
} from '../lib/api';
import { PageHeader } from '../components/ui';

const PROJECTS_KEY = '/api/v1/projects';

const PAGE_SIZE = 20;
type SearchTab = 'issues' | 'projects';

// ---------------------------------------------------------------------------
// Result area sub-components (extracted to reduce cognitive complexity)
// ---------------------------------------------------------------------------

interface IssuesResultAreaProps {
  readonly q: string;
  readonly isLoading: boolean;
  readonly error: unknown;
  readonly data: SearchResult | null;
  readonly from: number;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly onPage: (newFrom: number) => void;
}

function IssuesResultArea({ q, isLoading, error, data, from, currentPage, totalPages, onPage }: IssuesResultAreaProps) {
  if (!q) return <EmptyPrompt tab="issues" />;
  if (isLoading) return <Loading />;
  if (error) return <ErrorState />;
  if (!data || data.total === 0) return <NoResults q={q} />;
  return (
    <>
      <ResultsMeta total={data.total} from={from} size={PAGE_SIZE} />
      <ResultList hits={data.hits} />
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={PAGE_SIZE}
          onPage={onPage}
        />
      )}
    </>
  );
}

interface ProjectsResultAreaProps {
  readonly q: string;
  readonly allProjects: Project[] | null;
  readonly filteredProjects: Project[];
}

function ProjectsResultArea({ q, allProjects, filteredProjects }: ProjectsResultAreaProps) {
  if (!q) return <EmptyPrompt tab="projects" />;
  if (!allProjects) return <Loading />;
  if (filteredProjects.length === 0) return <NoResults q={q} />;
  return <ProjectResultList projects={filteredProjects} />;
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const qParam = searchParams.get('q') ?? '';
  const fromParam = parseInt(searchParams.get('from') ?? '0', 10) || 0;
  const tabParam = (searchParams.get('type') ?? 'issues') as SearchTab;

  // Local input value — starts from URL, can be edited before submitting.
  const [inputVal, setInputVal] = useState(qParam);

  // Keep input in sync when URL changes (e.g. browser back/forward).
  useEffect(() => {
    setInputVal(qParam);
  }, [qParam]);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = inputVal.trim();
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (tabParam !== 'issues') p.set('type', tabParam);
    navigate(`/search?${p.toString()}`, { replace: false });
  };

  const setTab = (tab: SearchTab) => {
    const p = new URLSearchParams(searchParams);
    if (tab === 'issues') p.delete('type');
    else p.set('type', tab);
    p.delete('from');
    navigate(`/search?${p.toString()}`);
  };

  // Issues tab — OpenSearch endpoint.
  const issuesApiKey = qParam && tabParam === 'issues'
    ? buildSearchURL({ q: qParam, from: fromParam, size: PAGE_SIZE })
    : null;
  const { data: issuesData, error: issuesError, isLoading: issuesLoading } =
    useSWR<SearchResult>(issuesApiKey, fetcher);

  // Projects tab — full list filtered client-side (no dedicated search endpoint).
  const { data: allProjects } = useSWR<Project[]>(
    tabParam === 'projects' ? PROJECTS_KEY : null,
    fetcher,
  );
  const filteredProjects = useMemo(() => {
    if (!allProjects || !qParam) return allProjects ?? [];
    const q = qParam.toLowerCase();
    return allProjects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.slug.toLowerCase().includes(q) ||
        p.repo_url.toLowerCase().includes(q),
    );
  }, [allProjects, qParam]);

  const goToPage = (newFrom: number) => {
    const p = new URLSearchParams(searchParams);
    if (newFrom > 0) p.set('from', String(newFrom));
    else p.delete('from');
    navigate(`/search?${p.toString()}`);
  };

  const totalPages = issuesData ? Math.ceil(issuesData.total / PAGE_SIZE) : 0;
  const currentPage = Math.floor(fromParam / PAGE_SIZE);

  return (
    <div className="max-w-4xl mx-auto p-8 w-full">
      <PageHeader eyebrow="orbital · search" title="Search" className="mb-4" />

      <div className="mb-6">
        <form onSubmit={handleSubmit} role="search" aria-label="Global search">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-fg-muted pointer-events-none"
                aria-hidden="true"
              />
              <input
                ref={inputRef}
                type="search"
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                placeholder="Search issues and projects…"
                aria-label="Search query"
                autoFocus
                className="
                  w-full pl-9 pr-4 py-2.5 rounded-lg border border-border
                  bg-bg-elevated text-fg text-sm placeholder-fg-subtle
                  focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent/60
                  transition-colors
                "
              />
            </div>
            <button
              type="submit"
              className="
                px-4 py-2.5 rounded-lg border border-accent/40
                bg-accent/10 text-accent text-sm font-semibold
                hover:bg-accent/20 transition-colors
              "
            >
              Search
            </button>
          </div>
        </form>

        {/* Tab bar */}
        <div className="mt-4 flex items-center gap-1 border-b border-border">
          {(['issues', 'projects'] as SearchTab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setTab(tab)}
              className={[
                'px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px',
                tabParam === tab
                  ? 'border-accent text-accent'
                  : 'border-transparent text-fg-muted hover:text-fg',
              ].join(' ')}
              aria-current={tabParam === tab ? 'page' : undefined}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {tabParam === 'issues' ? (
        <IssuesResultArea
          q={qParam}
          isLoading={issuesLoading}
          error={issuesError}
          data={issuesData ?? null}
          from={fromParam}
          currentPage={currentPage}
          totalPages={totalPages}
          onPage={goToPage}
        />
      ) : (
        <ProjectsResultArea
          q={qParam}
          allProjects={allProjects ?? null}
          filteredProjects={filteredProjects}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function EmptyPrompt({ tab }: { readonly tab: SearchTab }) {
  return (
    <div className="orb-card p-8 text-center text-fg-muted text-sm">
      {tab === 'projects'
        ? 'Type a query above and press Enter to filter projects by name or key.'
        : 'Type a query above and press Enter to search across all issues.'}
    </div>
  );
}

function Loading() {
  return (
    <div className="orb-card p-6 flex items-center justify-center gap-3 text-fg-muted">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">Searching…</span>
    </div>
  );
}

function ErrorState() {
  return (
    <div role="alert" className="orb-card p-5 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-1">search failed</p>
      <p className="text-sm text-fg">
        The search request failed. Make sure the OpenSearch cluster is reachable.
      </p>
    </div>
  );
}

function NoResults({ q }: { readonly q: string }) {
  return (
    <div className="orb-card p-8 text-center">
      <p className="font-display text-lg font-semibold text-fg mb-2">
        No issues found for &ldquo;{q}&rdquo;
      </p>
      <p className="text-fg-muted text-sm">
        Try different keywords or check the project&apos;s issue list directly.
      </p>
    </div>
  );
}

function ResultsMeta({
  total,
  from,
  size,
}: {
  readonly total: number;
  readonly from: number;
  readonly size: number;
}) {
  const start = from + 1;
  const end = Math.min(from + size, total);
  return (
    <p className="text-xs text-fg-muted mb-3">
      {total.toLocaleString()} result{total === 1 ? '' : 's'} — showing{' '}
      {start}–{end}
    </p>
  );
}

function ResultList({ hits }: { readonly hits: IssueHit[] }) {
  return (
    <ul className="orb-card divide-y divide-border" aria-label="Search results">
      {hits.map((hit) => (
        <SearchHitRow key={hit.id} hit={hit} />
      ))}
    </ul>
  );
}

const SEVERITY_TONE: Record<string, string> = {
  INFO: 'bg-bg-inset text-fg-muted border-border',
  MINOR: 'bg-accent-muted text-accent border-accent/30',
  MAJOR: 'bg-warning/10 text-warning border-warning/40',
  CRITICAL: 'bg-danger/10 text-danger border-danger/40',
  BLOCKER: 'bg-danger/20 text-danger border-danger/60',
};

const KIND_LABEL: Record<string, string> = {
  BUG: 'Bug',
  CODE_SMELL: 'Smell',
  VULNERABILITY: 'Vuln',
  SECURITY_HOTSPOT: 'Hotspot',
  SECRET: 'Secret',
};

function kindIcon(kind: string) {
  switch (kind as IssueKind) {
    case 'BUG': return <Bug className="h-3 w-3" aria-hidden="true" />;
    case 'CODE_SMELL': return <Sprout className="h-3 w-3" aria-hidden="true" />;
    case 'VULNERABILITY': return <ShieldAlert className="h-3 w-3" aria-hidden="true" />;
    case 'SECURITY_HOTSPOT': return <Eye className="h-3 w-3" aria-hidden="true" />;
    case 'SECRET': return <KeyRound className="h-3 w-3" aria-hidden="true" />;
    default: return null;
  }
}

function SearchHitRow({ hit }: { readonly hit: IssueHit }) {
  const tone = SEVERITY_TONE[hit.severity?.toUpperCase()] ?? SEVERITY_TONE.INFO;
  const kindLabel = KIND_LABEL[hit.kind?.toUpperCase()];
  const sevLabel = hit.severity?.toUpperCase() ?? 'INFO';
  const fileLine = hit.file_path
    ? `${hit.file_path}:${hit.line_start || 1}`
    : '—';

  // Link to the project's issues page filtered by nothing — there's no
  // direct /issues/:id route yet; a future GET /issues/:id endpoint
  // (Phase-3 follow-up) will let us link to the drawer directly.
  const projectHref = `/projects/${hit.project_id}/issues`;

  return (
    <li className="px-5 py-3 text-sm hover:bg-bg-hover transition-colors">
      <div className="flex items-start gap-3">
        <span
          className={`orb-badge border shrink-0 ${tone}`}
          title={`Severity: ${sevLabel}`}
        >
          {sevLabel}
        </span>
        {kindLabel && (
          <span className="orb-badge border bg-nebula/10 text-nebula border-nebula/30 shrink-0">
            {kindIcon(hit.kind?.toUpperCase())}
            <span>{kindLabel}</span>
          </span>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-fg font-medium leading-snug">
            {hit.message || <span className="text-fg-subtle">(no message)</span>}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-subtle">
            <span className="orb-code text-xs truncate max-w-[28rem]">
              {fileLine}
            </span>
            {hit.rule_id && (
              <span className="text-fg-muted">{hit.rule_id}</span>
            )}
            <Link
              to={projectHref}
              className="text-accent hover:underline truncate max-w-[12rem]"
              title={`Go to project ${hit.project_id}`}
            >
              project ↗
            </Link>
          </div>
        </div>
      </div>
    </li>
  );
}

function Pagination({
  currentPage,
  totalPages,
  pageSize,
  onPage,
}: {
  readonly currentPage: number;
  readonly totalPages: number;
  readonly pageSize: number;
  readonly onPage: (from: number) => void;
}) {
  return (
    <div className="mt-4 flex items-center justify-between text-xs text-fg-muted">
      <button
        type="button"
        disabled={currentPage === 0}
        onClick={() => onPage(Math.max(0, (currentPage - 1) * pageSize))}
        className="
          inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
          border border-border hover:bg-bg-hover transition-colors
          disabled:opacity-40 disabled:cursor-not-allowed
        "
        aria-label="Previous page"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        <span>Prev</span>
      </button>

      <span>
        Page {currentPage + 1} of {totalPages}
      </span>

      <button
        type="button"
        disabled={currentPage >= totalPages - 1}
        onClick={() => onPage((currentPage + 1) * pageSize)}
        className="
          inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
          border border-border hover:bg-bg-hover transition-colors
          disabled:opacity-40 disabled:cursor-not-allowed
        "
        aria-label="Next page"
      >
        <span>Next</span>
        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Project results (T271) — shown on the "Projects" tab
// ---------------------------------------------------------------------------

function ProjectResultList({ projects }: { readonly projects: Project[] }) {
  return (
    <div>
      <p className="text-xs text-fg-muted mb-3">
        {projects.length} project{projects.length === 1 ? '' : 's'} matched
      </p>
      <ul className="orb-card divide-y divide-border" aria-label="Project results">
        {projects.map((p) => (
          <ProjectResultRow key={p.id} project={p} />
        ))}
      </ul>
    </div>
  );
}

function ProjectResultRow({ project: p }: { readonly project: Project }) {
  return (
    <li className="px-5 py-3 text-sm hover:bg-bg-hover transition-colors">
      <Link to={`/projects/${p.id}`} className="flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-base font-semibold text-fg truncate">
              {p.name}
            </span>
            <span className="orb-code text-xs">{p.slug}</span>
          </div>
          {p.repo_url ? (
            <div className="text-xs text-fg-subtle truncate mt-0.5">{p.repo_url}</div>
          ) : null}
        </div>
        <div
          className="flex items-center gap-1 text-xs text-fg-muted shrink-0"
          title="Default branch"
        >
          <GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
          {p.default_branch}
        </div>
      </Link>
    </li>
  );
}

